import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/hooks/useTenant'
import { toast } from 'sonner'
import * as meetingsAPI from '@/api/one-on-ones'
import { meetingKeys } from './feedbackKeys'
import type { OneOnOneMeeting } from '@/types/performance'

/**
 * Use Upcoming Meetings Query
 *
 * Fetches upcoming 1-on-1 meetings for current user.
 * Cached for 1 minute.
 *
 * @example
 * ```tsx
 * const { data: meetings, isLoading } = useUpcomingMeetings({ status: 'confirmed', limit: 10 })
 * ```
 */
export function useUpcomingMeetings(params?: {
  status?: 'scheduled' | 'confirmed' | 'completed' | 'cancelled'
  limit?: number
}) {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: meetingKeys.list(tenantDomain!, params?.status),
    queryFn: async (): Promise<OneOnOneMeeting[]> => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      const result = await meetingsAPI.listMeetingsMe({
        status: params?.status || 'confirmed',
        limit: params?.limit || 10,
      })
      return Array.isArray(result?.items) ? result.items : []
    },
    enabled: !!tenantDomain,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Use Create Meeting Mutation
 *
 * Creates new 1-on-1 meeting with cache invalidation.
 *
 * @example
 * ```tsx
 * const createMeeting = useCreateMeeting()
 *
 * await createMeeting.mutateAsync({
 *   employeeId: 'emp-123',
 *   employeeName: 'John Doe',
 *   employeeRole: 'Developer',
 *   date: '2025-01-15',
 *   time: '09:00',
 *   duration: 30,
 *   recurrence: 'weekly'
 * })
 * ```
 */
export function useCreateMeeting() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      employeeId: string
      employeeName: string
      employeeRole: string
      date: string
      time: string
      duration: number
      recurrence: 'none' | 'weekly' | 'biweekly' | 'monthly'
    }) => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await meetingsAPI.createMeeting(data)
    },

    onSuccess: () => {
      if (!tenantDomain) return

      toast.success('1-on-1 agendado com sucesso!')

      // Invalidate meetings list to show new meeting
      queryClient.invalidateQueries({
        queryKey: meetingKeys.lists(),
      })
    },

    onError: (err) => {
      console.error('[CreateMeeting] Error:', err)
      toast.error(
        err instanceof Error ? err.message : 'Erro ao agendar 1-on-1'
      )
    },
  })
}
